import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";
import { gradeLabel } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicSAR() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/sar?token=${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Link invalid or expired"));
  }, [token]);

  if (err) return <div className="min-h-screen grid place-items-center p-8 text-center"><div><h1 className="text-2xl font-bold text-rose-800">{err}</h1><p className="text-slate-500 mt-2">Ask the provider to issue a new inspector link.</p></div></div>;
  if (!data) return <div className="p-10 text-slate-500">Loading shared SAR…</div>;

  const signMap = {};
  data.signoffs.forEach((s) => { if (s.decision === "approved") { signMap[s.theme_key] = signMap[s.theme_key] || []; signMap[s.theme_key].push(s); } });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-indigo-950 text-white py-5 border-b border-indigo-900">
        <div className="max-w-4xl mx-auto px-6 flex items-center gap-3">
          <ShieldCheck className="h-5 w-5" />
          <div>
            <div className="text-xs uppercase tracking-widest text-indigo-300">Inspector read-only view</div>
            <div className="font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>{data.share_label} · Self-Assessment Report</div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {data.themes.map((t) => {
          const acts = data.actions.filter((a) => a.theme_key === t.key);
          const evs = data.evidence.filter((e) => e.theme_key === t.key);
          const signs = signMap[t.key] || [];
          return (
            <Card key={t.key} className="p-6 border-slate-200">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t.code}</div>
                  <h2 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Plus Jakarta Sans" }}>{t.title}</h2>
                  <p className="text-sm text-slate-600 mt-1 max-w-2xl">{t.description}</p>
                </div>
                <Badge className={`grade-${t.current_grade} border`}>{gradeLabel(t.current_grade)}</Badge>
              </div>
              <div className="mt-5 whitespace-pre-wrap text-[15px] leading-relaxed text-slate-800 font-serif">{t.narrative || "No narrative recorded."}</div>
              {signs.length > 0 && (
                <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                  <b>Approved</b> by {signs[0].approver_name} · {new Date(signs[0].created_at).toLocaleDateString("en-GB")}
                </div>
              )}
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Linked actions ({acts.length})</div>
                  <ul className="space-y-1">{acts.map((a) => (<li key={a.id} className="text-slate-700 border-l-2 border-indigo-200 pl-2">{a.title} <span className="text-xs text-slate-500">— {a.progress || 0}%</span></li>))}{acts.length === 0 && <li className="text-slate-400 text-xs">None recorded.</li>}</ul>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Linked evidence ({evs.length})</div>
                  <ul className="space-y-1">{evs.map((e) => (<li key={e.id} className="text-slate-700 border-l-2 border-emerald-200 pl-2">{e.title} <span className="text-xs text-slate-500">— {e.source_type}</span></li>))}{evs.length === 0 && <li className="text-slate-400 text-xs">None recorded.</li>}</ul>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
